import React from 'react';
import { Box } from '@mui/material';
import Button from '@mui/material/Button';
import { type NextRing, useNextRing } from './useNextRing';
import { focusedFromKeyboard } from 'Utils/playAlong/tapInput';

type Visibility = 'hidden' | 'visible';

export interface TutorialNextButtonProps {
  /** As the Skip button's: undefined until first shown. */
  visibility?: Visibility;
  /** The Next ring running now, or null. */
  ring: NextRing | null;
  /** The tutorial is paused: the ring holds. */
  ringPaused: boolean;
  /** Next was tapped, or the ring closed: take the Skip path. */
  onNext: () => void;
  /** Keep practicing was tapped: the ring stops. */
  onKeepPracticing: () => void;
  /** Next's diameter in px, scaled with the video. */
  size: number;
  /**
   * Space presses a focused Next or Keep practicing. Off by default: Space is
   * the tutorial's pause key. On while taps are played, when Space plays
   * everywhere but on a focused control.
   */
  spacePresses?: boolean;
}

const BUTTON_COLOR = '#4A5AA1';
/** The ring sits this far outside Next, as a share of Next's diameter. */
const RING_GAP = 0.12;
/**
 * Next (with its ring) stays at least this far above the bottom of the video,
 * clear of the video's own controls (speed, volume, fullscreen).
 */
const VIDEO_CONTROLS_CLEARANCE_PX = 64;

/**
 * The Skip button of a tutorial the app cannot hear, labelled "Next". When the
 * objective's run is over a ring closes around it and then moves on by itself,
 * exactly as a tap on Next does. "Keep practicing" sits above it only while
 * the ring runs; tapping it stops the ring and Next waits for a tap.
 */
export default function TutorialNextButton({
  visibility,
  ring,
  ringPaused,
  onNext,
  onKeepPracticing,
  size,
  spacePresses = false,
}: TutorialNextButtonProps) {
  useNextRing({ ring, paused: ringPaused, onClosed: onNext });

  // Space toggles the tutorial's pause; it must not also press a focused button.
  // While taps play, Space presses a button only when it was focused from the
  // keyboard; after a click it is a tap, so the button must not take it.
  const keepSpaceForPause = (event: React.KeyboardEvent) => {
    if (event.key !== ' ') return;
    if (!spacePresses || !focusedFromKeyboard(event.currentTarget)) event.preventDefault();
  };
  const fontSize = Math.max(12, Math.round(size * 0.28));
  const ringInset = Math.round(size * RING_GAP);

  return (
    <Box
      id="tutorial-next-controls"
      className={visibility}
      sx={{
        position: 'absolute',
        bottom: `max(10%, ${VIDEO_CONTROLS_CLEARANCE_PX + ringInset}px)`,
        right: '10%',
        // Keep practicing stacks above Next, so it never reaches across into an
        // instruction shown at the bottom of the exercise.
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: `${Math.round(size * 0.35)}px`,
        opacity: 0,
        pointerEvents: visibility === 'visible' ? 'all' : 'none',
      }}
    >
      {ring && (
        <Button
          id="keep-practicing-button"
          variant="contained"
          onClick={onKeepPracticing}
          onKeyUp={keepSpaceForPause}
          sx={{
            fontFamily: 'Lato',
            fontWeight: 700,
            fontSize: `${fontSize}px`,
            textTransform: 'none',
            whiteSpace: 'nowrap',
            borderRadius: '999px',
            minHeight: `${Math.round(size * 0.6)}px`,
            padding: `0 ${Math.round(fontSize * 1.1)}px`,
            backgroundColor: '#FFFFFF',
            color: BUTTON_COLOR,
            '&:hover': { backgroundColor: '#E8EBF7' },
          }}
        >
          Keep practicing
        </Button>
      )}
      <Box sx={{ position: 'relative', width: `${size}px`, height: `${size}px` }}>
        <Button
          id="skip-button"
          variant="contained"
          onClick={onNext}
          onKeyUp={keepSpaceForPause}
          sx={{
            width: '100%',
            height: '100%',
            minWidth: 'unset',
            padding: 0,
            borderRadius: '50%',
            fontFamily: 'Lato',
            fontWeight: 700,
            fontSize: `${fontSize}px`,
            textTransform: 'none',
            backgroundColor: BUTTON_COLOR,
          }}
        >
          Next
        </Button>
        {ring && (
          <Box
            component="svg"
            data-testid="next-ring"
            aria-hidden
            viewBox="0 0 100 100"
            sx={{
              position: 'absolute',
              top: `-${ringInset}px`,
              left: `-${ringInset}px`,
              width: `${size + 2 * ringInset}px`,
              height: `${size + 2 * ringInset}px`,
              transform: 'rotate(-90deg)',
              pointerEvents: 'none',
            }}
          >
            <circle
              key={ring.id}
              className="next-ring-close"
              cx={50}
              cy={50}
              r={47}
              pathLength={100}
              fill="none"
              stroke="#FFFFFF"
              strokeWidth={5}
              strokeLinecap="round"
              strokeDasharray="100"
              style={{
                animationDuration: `${ring.durationMs}ms`,
                animationPlayState: ringPaused ? 'paused' : 'running',
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
